import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useAppDispatch from "@/hooks/useAppDispatch";
import { handleApiError } from "@/helpers/apiErrorHelper";
import wooStoreApiService from "@/api/services/wooStoreApiService";
import wooCustomApiService from "@/api/services/wooCustomApiService";
import { transformOrderTotals } from "@/helpers/helpers";
import type { OrderDetails } from "@/types/order";

interface OrderReceivedPageViewModel {
  order: OrderDetails | null;
  ecpayPaymentResult: {
    rtnCode: string | null;
    rtnMsg: string | null;
  };
  isLoading: boolean;
}

const useOrderReceivedPage = (): OrderReceivedPageViewModel => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const orderKey = searchParams.get("order_key");
  const email = searchParams.get("billing_email");

  // 綠界信用卡付款結果回傳資料，其他付款方式無此參數
  const ecpayPaymentResult = {
    rtnCode: searchParams.get("rtn_code"),
    rtnMsg: searchParams.get("rtn_msg"),
  };

  const navigate = useNavigate();
  const appDispatch = useAppDispatch();

  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchOrder = async (signal?: AbortSignal) => {
    if (!orderId || !orderKey || !email) return;
    appDispatch.setPageLoading(true);
    setIsLoading(true);

    const params = {
      key: orderKey,
      billing_email: email,
    };

    try {
      const [{ data: orderResult }, { data: orderAdditionalInfo }] = await Promise.all([
        wooStoreApiService.order.get(orderId, params, signal),
        wooCustomApiService.order.getAdditionInfo(Number(orderId), params, signal),
      ]);

      setOrder({
        ...orderResult,
        ...orderAdditionalInfo,
        totals: {
          ...orderResult.totals,
          ...transformOrderTotals(orderResult.totals),
        },
      });
    } catch (error) {
      if (signal?.aborted) return;

      switch (error?.data?.code) {
        case "woocommerce_rest_invalid_user": // 此包含未付款也無法查看
          appDispatch.pushAlert({
            type: "error",
            title: "此訂單無法查看 / 此訂單屬於其他顧客",
            autoClose: false,
          });
          break;

        case "woocommerce_rest_invalid_order":
          appDispatch.pushAlert({
            type: "error",
            title: error?.data?.message ?? "提供的訂單 ID 或金鑰無效",
            autoClose: false,
          });
          break;

        default:
          handleApiError(error, appDispatch, "取得訂單資料失敗，請稍後再試", { autoClose: false });
      }
    } finally {
      if (signal?.aborted) return;
      appDispatch.setPageLoading(false);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!orderId || !orderKey || !email) {
      navigate("/");
      return;
    }

    const controller = new AbortController();
    if (orderId && orderKey && email) {
      fetchOrder(controller.signal);
    }

    return () => {
      controller.abort();
    };
  }, [orderId, orderKey, email]);

  return { order, ecpayPaymentResult, isLoading };
};

export default useOrderReceivedPage;
